<template>
<v-card class="panel" elevation="1">
    <div class="subscriptions">
        <div class="page-header">
            <div class="left">
                <h2 class="page-title">{{ $page.props.lang.subscriptions || "Memberships" }}</h2>
            </div>
            <div class="right" v-show="$page.props.p['members.manage']">
                <v-btn color="primary" dark @click="subModal = true">
                    <v-icon small>mdi-plus</v-icon>
                    <span v-if="$vuetify.breakpoint.mdAndUp" class="ml-2">{{ $page.props.lang.addSubscription || "Add membership" }}</span>
                </v-btn>
            </div>
        </div>
        <v-dialog v-model="subModal" max-width="500px" content-class="new-subscription">
            <v-card>
                <div class="modal-header">
                    <h4 class="modal-headline">{{ $page.props.lang.addSubscription || "Add membership" }}</h4>
                    <div class="tools">
                        <v-btn icon @click="subModal = false"><v-icon>mdi-close</v-icon></v-btn>
                    </div>
                </div>
                <v-card-text>
                    <v-container>
                        <v-row>
                            <v-col cols="12">
                                <label class="custom-label">{{ $page.props.lang.subscriptions || 'Select membership' }}</label>
                                <v-select
                                v-model="newSub.subscription_id"
                                @change="subscriptionSelected"
                                :items="subscriptionList"
                                item-text="name"
                                item-value="id"
                                outlined dense hide-details></v-select>
                            </v-col>

                            <div v-show="showModalForm" class="wrap-hidden">
                                <v-col cols="12">
                                    <label class="custom-label">{{ $page.props.lang.startDate || 'Start Date' }}</label>
                                    <v-menu
                                    v-model="startDatePicker"
                                    :close-on-content-click="false"
                                    :return-value.sync="date"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="290px">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-text-field :value="newSub.start_date | formatDate"
                                            append-icon="mdi-menu-down" readonly v-bind="attrs" v-on="on"
                                            @click:append="startDatePicker = true" outlined dense hide-details></v-text-field>
                                        </template>
                                        <v-date-picker
                                        v-model="newSub.start_date"
                                        :first-day-of-week="1" no-title scrollable color="primary">
                                            <v-spacer></v-spacer>
                                            <v-btn color="regular" @click="startDatePicker = false">{{ $page.props.lang.cancel || "Cancel" }}</v-btn>
                                            <v-btn color="primary" @click="setSubEndDate">{{ $page.props.lang.ok || "Ok" }}</v-btn>
                                        </v-date-picker>
                                    </v-menu>
                                </v-col>
                                <v-col cols="12">
                                    <label class="custom-label">{{ $page.props.lang.price || 'Price' }}</label>
                                    <v-text-field
                                        v-model="newSub.mem_price" type="number" :suffix="$page.props.organization.settings.currency"
                                        outlined dense solo flat
                                        :error-messages="responseErrors.mem_price"
                                        @focus="responseErrors.mem_price=''"
                                        :hide-details="!responseErrors.mem_price"></v-text-field>
                                </v-col>
                                <v-col cols="12">
                                    <v-switch v-model="newSub.limit_sessions" :label="$page.props.lang.limitSessions || 'Limit Sessions'"></v-switch>
                                    <label v-if="newSub.limit_sessions" class="custom-label">{{ $page.props.lang.limit || 'Total Sessions' }}</label>
                                    <v-text-field v-if="newSub.limit_sessions"
                                            v-model="newSub.total_sessions" type="number"
                                            outlined dense solo flat
                                            :error-messages="responseErrors.total_sessions"
                                            @focus="responseErrors.total_sessions=''"
                                            :hide-details="!responseErrors.total_sessions"></v-text-field>
                                </v-col>
                                <v-col cols="12">
                                    <v-switch v-model="newSub.is_limited" :label="$page.props.lang.limit_class_access || 'Limit Class Access'"></v-switch>
                                    <label v-if="newSub.is_limited" class="custom-label">{{$page.props.lang.select_activity_type || 'Select activity type:'}}</label>
                                    <div v-if="newSub.is_limited">
                                        <v-checkbox v-for="course in courses"
                                                v-bind:key="course.id"
                                                v-model="newSub.courses"
                                                dense
                                                hide-details
                                                :label="course.title"
                                                :value="course.id"></v-checkbox>
                                    </div>
                                </v-col>
                                <v-col cols="auto">
                                <v-switch v-model="newSub.is_paid"
                                    v-show="$page.props.p['members.payments.make']"
                                    :label="$page.props.lang.markAsPaid || 'Mark as paid'"></v-switch>
                                <label v-if="newSub.is_paid" class="custom-label">{{ $page.props.lang.paymentMethod || 'Payment type' }}</label>
                                <v-select v-if="newSub.is_paid"
                                    class="fit"
                                    v-model="newSub.pay_method"
                                    :items="paymentTypes"
                                    item-text="name"
                                    item-value="id"
                                    :error-messages="responseErrors.pay_method"
                                    @focus="responseErrors.pay_method=''"
                                    :hide-details="!responseErrors.pay_method"
                                    outlined dense>
                                </v-select>
                                </v-col>
                        </div>
                        </v-row>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn color="regular" @click="subModal = false">{{ $page.props.lang.cancel || "Cancel" }}</v-btn>
                            <v-btn color="primary" :loading="saving" @click="saveSubscription" v-if="showModalForm">{{ $page.props.lang.save || "Save" }}</v-btn>
                        </v-card-actions>
                    </v-container>
                </v-card-text>
            </v-card>
        </v-dialog>
        <div class="table-container">
            <v-data-table
            :headers="subscriptionsHeaders"
            :items="memberData.subscriptions"
            :page.sync="tablePage"
            :items-per-page="itemsPerPage"
            @page-count="tablePageCount = $event"
            item-key="item.id"
            mobile-breakpoint="0"
            hide-default-footer
            >
                <template v-slot:item.name="{ item }">
                <inertia-link :href="'/backoffice/members/details/'+ memberData.id +'/subscription/' + item.id" class="name">
                    {{ item.name }}
                </inertia-link>
                </template>
                <template v-slot:item.start_date="{ item }">
                    {{ item.current_period }}
                </template>
                <template v-slot:item.mem_price="{ item }">
                        {{ item.mem_price || item.price }} <span class="table-currency">{{ $page.props.organization.settings.currency }}</span>
                        <v-chip v-if="item.contains_unpaid" class="ml-2" color="red" text-color="white" stlyle="top:12px; right:6px;" small>
                            <v-icon class="mr-2" small color="white"> mdi-currency-usd-off </v-icon>
                            {{$page.props.lang.unpaid || "Unpaid"}}
                        </v-chip>
                        <v-chip v-else class="ml-2" color="#4ede77" text-color="white" stlyle="top:12px;" small>
                        <v-icon class="mr-1" small color="white">mdi-currency-usd</v-icon>
                        {{ $page.props.lang.paid || "Paid"}}
                        </v-chip>
                        <v-chip v-if="item.paused == 1" class="ml-2" color="warning" text-color="white" stlyle="top:12px; right:6px; font-size:18px" small>
                            <v-icon class="mr-2" small color="white">mdi-pause-circle-outline</v-icon>
                            {{ $page.props.lang.paused || "Paused"}}
                        </v-chip>
                        <v-chip v-if="item.status == 0" class="ml-2" color="warning" text-color="white" stlyle="top:12px; right:6px;" small>
                            <v-icon class="mr-2" small color="white">mdi-cancel</v-icon>
                            {{ $page.props.lang.canceled || "Canceled"}}
                        </v-chip>
                </template>
                <template v-slot:item.mem_limit="{ item }">
                    <span v-if="!item.limit_sessions">∞</span>
                    <span v-if="item.limit_sessions">{{ item.remaining_sessions }}  / {{ item.total_sessions || item.total_sessions }}</span>
                </template>
                <template v-slot:item.action="{ item }">
                    <v-btn v-if="item.can_delete" @click="openDelSubModal(item.id)" :id="'delete' + item.id" v-show="$page.props.p['members.manage']" icon class="delete-icon">
                        <v-icon>mdi-trash-can-outline</v-icon>
                        <v-tooltip :activator="'#delete'+ item.id" bottom open-delay="500">
                            <span>{{$page.props.lang.deleteSubscription || "Delete subscription"}}</span>
                        </v-tooltip>
                    </v-btn>
                </template>
            </v-data-table>

            <ConfirmDelete :open="deleteSubModal" @no="deleteSubModal = false" @yes="deleteMemberSubscription"></ConfirmDelete>
        </div>
        <div class="table-container mt-4">
            <h3>{{lang.inactiveSubs || 'Inactive'}}</h3>
            <v-data-table
            :headers="subscriptionsPastHeaders"
            :items="completedSubscriptions"
            :page.sync="tablePage"
            :items-per-page="itemsPerPage"
            @page-count="tablePageCount = $event"
            item-key="item.id"
            mobile-breakpoint="0"
            hide-default-footer
            >
                <template v-slot:item.name="{ item }">
                <inertia-link :href="'/backoffice/members/details/'+ memberData.id +'/subscription/' + item.id" class="name">
                    {{ item.name }}
                </inertia-link>
                </template>
                <template v-slot:item.mem_price="{ item }">
                        {{ item.mem_price || item.price }} <span class="table-currency">{{ $page.props.organization.settings.currency }}</span>
                        <v-chip v-if="item.contains_unpaid" class="ml-2" color="red" text-color="white" stlyle="top:12px; right:6px;" small>
                            <v-icon class="mr-2" small color="white"> mdi-currency-usd-off </v-icon>
                            {{$page.props.lang.unpaid || "Unpaid"}}
                        </v-chip>
                        <v-chip v-else class="ml-2" color="#4ede77" text-color="white" stlyle="top:12px;" small>
                        <v-icon class="mr-1" small color="white">mdi-currency-usd</v-icon>
                        {{ $page.props.lang.paid || "Paid"}}
                        </v-chip>
                        <v-chip v-if="item.paused == 1" class="ml-2" color="warning" text-color="white" stlyle="top:12px; right:6px; font-size:18px" small>
                            <v-icon class="mr-2" small color="white">mdi-pause-circle-outline</v-icon>
                            {{ $page.props.lang.paused || "Paused"}}
                        </v-chip>
                        <v-chip v-if="item.status == 0" class="ml-2" color="warning" text-color="white" stlyle="top:12px; right:6px;" small>
                            <v-icon class="mr-2" small color="white">mdi-cancel</v-icon>
                            {{ $page.props.lang.canceled || "Canceled"}}
                        </v-chip>
                </template>
                <template v-slot:item.mem_limit="{ item }">
                    <span v-if="!item.limit_sessions">∞</span>
                    <span v-if="item.limit_sessions">{{ item.remaining_sessions }}  / {{ item.total_sessions || item.total_sessions }}</span>
                </template>
                <template v-slot:item.action="{ item }">
                    <v-btn v-if="item.can_delete" @click="openDelSubModal(item.id)" :id="'delete' + item.id" v-show="$page.props.p['members.manage']" icon class="delete-icon">
                        <v-icon>mdi-trash-can-outline</v-icon>
                        <v-tooltip :activator="'#delete'+ item.id" bottom open-delay="500">
                            <span>{{$page.props.lang.deleteSubscription || "Delete subscription"}}</span>
                        </v-tooltip>
                    </v-btn>
                </template>
            </v-data-table>
            <v-pagination v-model="tablePage" :length="tablePageCount" class="front-paginate"></v-pagination>
        </div>
        <v-dialog v-model="paymentModal"
        max-width="500px" content-class="payment">
            <v-card>
                <div class="modal-header">
                    <h4 class="modal-headline">{{ $page.props.lang.assignPayment || "Add payment" }}</h4>
                    <div class="tools">
                        <v-btn icon @click="paymentModal = false"><v-icon>mdi-close</v-icon></v-btn>
                    </div>
                </div>
                <v-card-text>
                    <v-container>
                        <div class="fields">
                            <div class="grid">
                                <p class="custom-label">{{ $page.props.lang.amount|| 'Amount' }}</p>
                                <v-text-field v-model="payment.total_price"
                                :suffix="$page.props.organization.settings.currency"
                                outlined dense :hide-details="!responseErrors.total_price"
                                :error-messages="responseErrors.total_price"
                                @focus="responseErrors.total_price=''"></v-text-field>
                                <p class="custom-label">{{ $page.props.lang.paymentMethod||'Payment method' }}</p>
                                <v-select v-model="payment.payment_type_id"
                                :items="paymentTypes" item-text="name" item-value="id"
                                outlined dense :hide-details="!responseErrors.payment_type_id"
                                :error-messages="responseErrors.payment_type_id"
                                @focus="responseErrors.payment_type_id=''"></v-select>
                                <p class="custom-label">{{ $page.props.lang.description || 'Description' }}</p>
                                <v-textarea v-model="payment.description"
                                outlined dense rows="2" hide-details></v-textarea>
                            </div>
                        </div>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn color="regular" @click="paymentModal = false">{{ $page.props.lang.cancel || "Cancel" }}</v-btn>
                            <v-btn @click="assignPayment" color="primary">{{ $page.props.lang.save || "Save" }}</v-btn>
                        </v-card-actions>
                    </v-container>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
    </v-card>
</template>

<style scoped>
  .v-select.fit {
    width: 250px !important;
  }
</style>

<script>
import AppLayout from '@/js/Layouts/AppLayout'
import MemberLayout from './Layouts/Default'
import ConfirmDelete from '@/js/Components/ConfirmDelete'
import EventBus from '@/js/eventbus'

export default {
    metaInfo() {
        return {
            title: this.$page.props.member.full_name + ' - '+ (this.$page.props.lang.title || "Memberships")
        }
    },
	name: "MemberSubscriptions",
	components: {
		ConfirmDelete
	},
    layout: (h, page) => {
      return h(AppLayout, [
        h(MemberLayout, [page]),
      ])
    },
    props: {
        member: Object,
        courses: Array,
        subscriptionList: Array,
        paymentTypes: Array,
        errors: Object,
        lang: Object
    },
	data() {
		return {

            memberData: this.member,
            completedSubscriptions: [],
            saving: false,
            date: moment().format('YYYY-MM-DD'),
            subscriptionsHeaders: [
                {text: this.lang.name || 'Name', value: 'name', align: 'start'},
                {text: this.lang.current_period || 'Current period', value: 'start_date', sortable: false},
                {text: this.lang.price || 'Price', value: 'mem_price', sortable: false},
                {text: '', value: 'action', sortable: false, align: 'end'}
            ],
            subscriptionsPastHeaders: [
                {text: this.lang.name || 'Name', value: 'name', align: 'start'},
                {text: this.lang.price || 'Price', value: 'mem_price', sortable: false},
                {text: '', value: 'action', sortable: false, align: 'end'}
            ],
            subscriptionActiveSelect: [
                {text: this.lang.activeSubs || 'Active', value: 'active'},
                {text: this.lang.inactiveSubs || 'Inactive', value: 'completed'}
            ],
            statusSelect: 'active',
            subModal: false,
            showModalForm: false,
            newSub: {
                member_id: this.member.id,
                subscription_id: '',
                description: '',
                start_date: moment().format('YYYY-MM-DD'),
                mem_price: null,
                total_sessions: null,
                limit_sessions: false,
                send_email: false,
                is_paid: false,
                is_limited:false,
                courses: [],
                coursesList: [],
                pay_method: null
            },
            resetSub: {
                member_id: this.member.id,
                subscription_id: '',
                description: '',
                start_date: moment().format('YYYY-MM-DD'),
                end_date: moment().format('YYYY-MM-DD'),
                mem_price: null,
                total_sessions: null,
                limit_sessions: false,
                total_sessions: null,
                is_limited: false,
                courses: [],
                is_paid: false
            },
            startDatePicker: false,
            endDatePicker: false,
            renewDatePicker: false,
            messageObjects: [],
            responseErrors: {},
            deleteSubModal: false,
            subForDeleteId: null,
            paymentModal: false,
            renewModal: false,
            subForRenew: {renew_type: 0},
            subForRenewOrgEndDate: null,
            renewTypeSelectItems: [
                {text: this.lang.fromToday || 'From today', value: 0},
                {text: this.lang.fromEndDate || 'From end date', value: 1}
            ],
            tablePage: 1,
            tablePageCount: 0,
            itemsPerPage: 8,
            payment: {},
            paymentMethods: [
                { title: 'Cash', value: 'cash'},
                { title: 'Card', value: 'card'},
            ]
		}
	},
    computed: {
        gender() {
            let male = this.lang.male ||'Male'
            let female = this.lang.female || 'Female'
            let other = this.lang.other || 'Other'
            return [
                { text: male, value: 0 },
                { text: female, value: 1 },
                { text: other, value: 2 }

            ]
        },
        formattedDate() {
            let realDate = new Date(this.form.birth_date)
            let month = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][realDate.getMonth()]
            let day = realDate.getDate()
            let year = realDate.getFullYear()
            return day + '. ' + month + ' ' + year + ''
        }
    },
    watch: {
        subModal: function (value) {
            if (value===false) {
                this.newSub = Object.assign({}, this.resetSub)
                this.showModalForm = false
            }
        },
        member: function (value) {
            this.memberData = value
        }
    },
    created(){
        this.getCompletedSubscriptions();
    },
    methods: {
        paymentAvailable(item) {
            if (item.status===0) {
                return true
            }
        },
        openPaymentModal(item) {
            this.paymentModal = true
            this.payment.description = ''
            this.payment.payment_type_id = null
            this.payment.id = item.id
            this.payment.invoice_id = item.invoice_id
            this.payment.total_price = item.mem_price || item.price
        },
        assignPayment() {
            axios.post('/backoffice/members/assignPayment', this.payment)
                .then(response => {
                    if(response.data.status==="OK") {
                        this.generatePdfInvoice(this.payment.invoice_id)
                        this.$inertia.reload({only:['member']})
                        EventBus.$emit('SHOW_NOTIFICATION',{
                                message: response.data.message,
                                color: 'success',
                                timeout: 5000
                            })
                        this.paymentModal = false
                    }
                })
                .catch(error => {
                    EventBus.$emit('SHOW_NOTIFICATION',{
                            message: error.response.data.message,
                            color: 'error',
                            timeout: 10000
                        })
                    this.responseErrors = error.response.data.errors
                })
        },
        renewAvailable(item) {
            let now = moment()
            let end = moment(item.end_date)
            let difference = end.diff(now, 'days')
            if (difference < 6 || item.remaining_sessions < 2) {
                return true
            }
        },
        setRenewEndDate() {
            let sub = this.subForRenew
            if(this.subForRenew.renew_type===0) {
                this.subForRenew.start_date = new Date().toISOString().substr(0, 10)
            } else if (this.subForRenew.renew_type===1) {
                this.subForRenew.start_date = this.subForRenewOrgEndDate
            }
            if (sub.period_name === "Year") {
                sub.end_date = moment(moment(sub.start_date)).add(sub.period_value, 'years').format("YYYY-MM-DD")
            } else if (sub.period_name === "Month") {
                sub.end_date = moment(moment(sub.start_date)).add(sub.period_value, 'months').format("YYYY-MM-DD")
            } else if (sub.period_name === "Week") {
                sub.end_date = moment(moment(sub.start_date)).add(sub.period_value, 'weeks').format("YYYY-MM-DD")
            } else if (sub.period_name === "Day") {
                sub.end_date = moment(moment(sub.start_date)).add(sub.period_value, 'days').format("YYYY-MM-DD")
            }
        },
        subscriptionsStatus() {
            axios.get('/backoffice/members/subscriptions/get/'+this.member.id+'/active')
                .then(response => {
                    if (response.data.status==="OK") {

                         this.memberData.subscriptions = Object.assign([], response.data.subscriptions)
                        for(let i=0;i<this.memberData.subscriptions.length;i++) {
                            this.memberData.subscriptions[i].id = response.data.subscriptions[i].id
                        }
                    }
                })
        },
        getCompletedSubscriptions(){
             axios.get('/backoffice/members/subscriptions/get/'+this.member.id+'/completed')
                .then(response => {
                    if (response.data.status==="OK") {
                        this.completedSubscriptions = Object.assign([], response.data.subscriptions)
                    }
                })
        },
        setSubEndDate() {
            let sub = this.subscriptionList.filter(item => item.id===this.newSub.subscription_id)[0]
            this.newSub.end_date = this.newSub.start_date
            if (sub.period_name === "Year") {
                this.newSub.end_date = moment(moment(this.newSub.end_date)).add(sub.period_value, 'years').format("YYYY-MM-DD")
            } else if (sub.period_name === "Month") {
                this.newSub.end_date = moment(moment(this.newSub.end_date)).add(sub.period_value, 'months').format("YYYY-MM-DD")
            } else if (sub.period_name === "Week") {
                this.newSub.end_date = moment(moment(this.newSub.end_date)).add(sub.period_value, 'weeks').format("YYYY-MM-DD")
            } else if (sub.period_name === "Day") {
                this.newSub.end_date = moment(moment(this.newSub.end_date)).add(sub.period_value, 'days').format("YYYY-MM-DD")
            }
            this.startDatePicker = false
        },
        subscriptionSelected() {
            let sub = this.subscriptionList.filter(item => item.id===this.newSub.subscription_id)[0]
            this.newSub = Object.assign({}, this.resetSub);
            this.newSub.subscription_id = sub.id;
            this.newSub.mem_price = sub.price;
            this.newSub.limit_sessions = sub.limit ? true : false;
            this.newSub.total_sessions = sub.limit;
            this.newSub.description = sub.description;
            this.newSub.is_limited = sub.is_limited;
            this.newSub.courses = sub.courses.map(x => x.course_id);
            this.setSubEndDate()
            this.showModalForm = true;
        },
        saveSubscription() {

            this.saving  = true;
            let newSubPack = Object.assign({ member_id: this.member.id}, this.newSub);
            // newSubPack = {
            //     member_id: this.member.id,
            //     subscription_id: this.newSub.subscription_id,
            //     description: this.newSub.description,
            //     start_date: this.newSub.start_date,
            //     mem_price: this.newSub.mem_price != '' ? Number(this.newSub.mem_price) : null,
            //     total_sessions: this.newSub.total_sessions != null ? Number(this.newSub.total_sessions) : null,
            //     limit_sessions: this.newSub.limit_sessions,
            //     is_paid: this.newSub.is_paid ? true : false,
            //     pay_method: this.newSub.is_paid ? this.newSub.pay_method : null,
            //     is_limi
            // }
            axios.post('/backoffice/members/addMemberSubscription', newSubPack)
                .then(response => {

                    this.saving = false;
                    if(response.data.status==="OK") {

                        this.subModal = false;
                        this.$inertia.visit('/backoffice/members/details/' + response.data.membership[0]['member_id'] + '/subscription/' + response.data.membership[0]['id'], { method: 'get' })
                        EventBus.$emit('SHOW_NOTIFICATION',{
                                message: response.data.message,
                                color: 'success',
                                timeout: 5000
                            })
                    }
                })
                .catch(error => {
                    this.saving = false;
                   EventBus.$emit('SHOW_NOTIFICATION',{
                            message: error.response.data.message,
                            color: 'error',
                            timeout: 10000
                        })
                    this.responseErrors = error.response.data.errors
                })
        },
        generatePdfInvoice(invoice) {

            window.open('/backoffice/members/generatePdfInvoice/' + invoice +'/' + this.member.id + '/false', '_blank')
        },
        openDelSubModal(itemId) {
            this.deleteSubModal = true
            this.subForDeleteId = itemId
        },
        deleteMemberSubscription() {
            let deleteMembSubPack = {
                member_id: this.member.id,
                id: this.subForDeleteId
            }
            axios.post('/backoffice/members/deleteSubscription', deleteMembSubPack)
                .then(response => {
                    if(response.data.status==="OK") {
                        this.$inertia.reload({only:['member']})
                        EventBus.$emit('SHOW_NOTIFICATION',{
                                message: response.data.message,
                                color: 'success',
                                timeout: 5000
                            })
                        this.subModal = false
                    }
                })
                .catch(function(error) {
                    EventBus.$emit('SHOW_NOTIFICATION',{
                            message: error.response.data.message,
                            color: 'error',
                            timeout: 10000
                        })
                    this.responseErrors = error.response.data.errors
                })
            this.deleteSubModal = false
        }
    }
}
</script>
